import { Observable, Subject } from 'rxjs';

import { Inject, Injectable } from '@angular/core';

import { SnLonLat, SnLonLatLike } from '@lib/geo/helpers';
import { IMarker as _IMarker } from '@lib/geo/map';
import { MapMarkerIcon } from '@lib/geo/map/core/marker/icon-marker-options';
import { IMapMarkerStyleOptions } from '@lib/geo/map/core/marker/marker-options';
import { FsMapPopup } from '@lib/geo/map/core/popup/map-popup';
import { IMarkerFacade } from '@lib/geo/map/interfaces/markers/map-marker-facade.interface';
import {
  IMarkerAddEvent,
  IMarkerClickEvent,
  IMarkerDestroyEvent,
  IMarkerDoubleClickEvent,
  IMarkerDragEndEvent,
  IMarkerDragMoveEvent,
  IMarkerDragStartEvent,
  IMarkerEventUnion,
  IMarkerIconChangeEvent,
  IMarkerMouseEnterEvent,
  IMarkerMouseLeaveEvent,
  IMarkerMouseOutEvent,
  IMarkerMouseOverEvent,
  IMarkerPositionChangeEvent,
  IMarkerRemoveEvent,
  IMarkerRightClickEvent,
  IMarkerSelectionChangeEvent,
  IMarkerStyleChangeEvent,
  IMarkerTitleChangeEvent,
  IMarkerVisibilityChangeEvent,
} from '@lib/geo/map/interfaces/markers/marker-events.interface';
import { IMarkerOptions } from '@lib/geo/map/interfaces/markers/marker-options.interface';
import { DistanceUnit, MapGeometryFacadeService } from '@lib/map-geometry';
import { SnBadgeConfig } from '@lib/ui/badge';

import { EsriMap } from '../map';
import { IAdvancedEsriFeatureOptions } from '../overlays/markers/advanced-esri-feature-marker';
import { IEsriMarker } from '../overlays/markers/interfaces/esri-marker.interface';
import { EsriMarkerBadgeService } from '../overlays/markers/services/esri-marker-badge.service';
import { EsriMarkerEventService } from '../overlays/markers/services/esri-marker-event.service';
import { EsriMarkerFactoryService } from '../overlays/markers/services/esri-marker-factory.service';
import { EsriMarkerGeometryService } from '../overlays/markers/services/esri-marker-geometry.service';
import { EsriMarkerQueryService } from '../overlays/markers/services/esri-marker-query.service';
import { EsriMarkerStyleService } from '../overlays/markers/services/esri-marker-style.service';
import { ESRI_MODULES_TOKEN, EsriModules } from '../tokens';

type IMarker = _IMarker<EsriMap>;

/**
 * Adapter class that implements IMarkerFacade for ESRI Map markers.
 *
 * Bridges between the unified IMarkerFacade interface and ESRI implementation.
 * Uses IEsriMarker instances with embedded IMarker data for state management.
 */
@Injectable()
export class EsriMarkerAdapter implements IMarkerFacade<EsriMap> {
  private readonly _esriMarkers = new Map<string, IEsriMarker>();
  private readonly _destroy$ = new Subject<void>();
  private _map?: EsriMap;

  constructor(
    private readonly _markerFactory: EsriMarkerFactoryService,
    private readonly _badgeService: EsriMarkerBadgeService,
    private readonly _eventService: EsriMarkerEventService,
    private readonly _geometryService: EsriMarkerGeometryService,
    private readonly _queryService: EsriMarkerQueryService,
    private readonly _styleService: EsriMarkerStyleService,
    private readonly _mapGeometryService: MapGeometryFacadeService<EsriMap>,
    @Inject(ESRI_MODULES_TOKEN) private readonly esri: EsriModules,
  ) {
    this._queryService.setMarkersCollection(this._esriMarkers);
    this._eventService.setMarkersCollection(this._esriMarkers);
    this._geometryService.setMarkersCollection(this._esriMarkers);
    this._mapGeometryService.setMap(this._map);
  }

  public setMap(map: EsriMap): void {
    this._map = map;
    this._mapGeometryService.setMap(map);
  }

  public addMarker(markerOptions: IMarkerOptions): IMarker {
    const markerId = this._generateMarkerId();
    const esriMarkerOptions = this._convertToEsriMarkerOptions(markerOptions);
    const esriMarker = this._createEsriMarker(esriMarkerOptions);
    const markerData = this._createMarkerData(markerId, markerOptions, esriMarker);

    (esriMarker as IEsriMarker & { markerData: IMarker }).markerData = markerData;
    this._esriMarkers.set(markerId, esriMarker);
    if (this._map) {
      esriMarker.addTo(this._map);
    }
    this._eventService.fireMarkerEvent('add', markerData, { reason: 'programmatic' });

    return markerData;
  }

  public removeMarker(marker: IMarker): void {
    const markerId = marker.id;
    const esriMarker = this._esriMarkers.get(markerId);

    if (esriMarker) {
      this._eventService.fireMarkerEvent('remove', marker, { reason: 'programmatic' });
      esriMarker.remove();
      this._esriMarkers.delete(markerId);
      this._eventService.fireMarkerEvent('destroy', marker, { reason: 'programmatic' });
    }
  }

  public updateMarker(marker: IMarker, options: Partial<IMarkerOptions>): void {
    const markerId = marker.id;
    const esriMarker = this._esriMarkers.get(markerId);

    if (esriMarker) {
      if (options.lonLat) {
        this.setPosition(marker, SnLonLat.convert(options.lonLat));
      }
      if (options.icon) {
        this.setIcon(marker, options.icon);
      }
      if (options.style) {
        this.setStyle(marker, options.style);
      }
      if (options.draggable !== undefined) {
        this.setDraggable(marker, options.draggable);
      }
      if (options.rotation !== undefined) {
        this.setRotation(marker, options.rotation);
      }
      if (options.title) {
        this.setTitle(marker, options.title);
      }

      Object.assign(marker, options);
      marker.lastModified = new Date();
      marker.version++;
    }
  }

  public setVisible(marker: IMarker, visible: boolean): void {
    const esriMarker = this._esriMarkers.get(marker.id);
    if (esriMarker) {
      if (esriMarker.setVisible && typeof esriMarker.setVisible === 'function') {
        esriMarker.setVisible(visible);
      }
      marker.visible = visible;
    }
  }

  public setPosition(marker: IMarker, position: SnLonLat): void {
    const esriMarker = this._esriMarkers.get(marker.id);
    if (esriMarker) {
      if (esriMarker.setLonLat && typeof esriMarker.setLonLat === 'function') {
        esriMarker.setLonLat(position);
      }
      marker.position = position;
    }
  }

  public setIcon(marker: IMarker, icon: MapMarkerIcon): void {
    const esriMarker = this._esriMarkers.get(marker.id);
    if (esriMarker) {
      const previousIcon = marker.icon;
      esriMarker.setIcon(icon as any);
      marker.icon = icon;

      // Icon change events are now handled by the event service
    }
  }

  public setStyle(marker: IMarker, style: IMapMarkerStyleOptions): void {
    const esriMarker = this._esriMarkers.get(marker.id);
    if (esriMarker) {
      const previousStyle = marker.styles;
      this._styleService.setStyle(esriMarker, marker, style);

      this._eventService.fireMarkerEvent('stylechange', marker, {
        current: style,
        previous: previousStyle,
        changed: true,
      });
    }
  }

  public setDraggable(marker: IMarker, draggable: boolean): void {
    const esriMarker = this._esriMarkers.get(marker.id);
    if (esriMarker) {
      this._styleService.setDraggable(esriMarker, marker, draggable);

      this._eventService.fireMarkerEvent('draggablechange', marker, {
        current: draggable,
        previous: !draggable,
        changed: true,
      });
    }
  }

  public setRotation(marker: IMarker, rotation: number): void {
    const esriMarker = this._esriMarkers.get(marker.id);
    if (esriMarker) {
      this._styleService.setRotation(esriMarker, marker, rotation);

      this._eventService.fireMarkerEvent('rotationchange', marker, {
        current: rotation,
        previous: marker.rotation,
        changed: true,
      });
    }
  }

  public setTitle(marker: IMarker, title: string): void {
    const esriMarker = this._esriMarkers.get(marker.id);
    if (esriMarker) {
      const previousTitle = marker.title;
      this._styleService.setTitle(esriMarker, marker, title);

      if (previousTitle !== title) {
        this._eventService.fireMarkerEvent('titlechange', marker, {
          current: title,
          previous: previousTitle,
          changed: true,
        });
      }
    }
  }

  public getMarkerId(marker: IMarker): string {
    return marker.id;
  }

  public getMarkerPosition(marker: IMarker): SnLonLat {
    return marker.position;
  }

  public getMarkerTitle(marker: IMarker): string {
    return marker.title;
  }

  public getMarkerAttributes(marker: IMarker): Record<string, unknown> {
    return marker.attributes || {};
  }

  public getMarkerCenter(marker: IMarker): { center: number[] } {
    return { center: [marker.position.lon, marker.position.lat] };
  }

  public getMarkerGeometry(marker: IMarker): unknown {
    const esriMarker = this._esriMarkers.get(marker.id);
    if (!esriMarker) {
      throw new Error(`Marker with id ${marker.id} not found`);
    }
    return esriMarker;
  }

  public setBadge(marker: IMarker, badge: SnBadgeConfig): void {
    const esriMarker = this._esriMarkers.get(marker.id);
    if (esriMarker) {
      this._badgeService.setBadge(esriMarker, marker, badge);
    }
  }

  public getBadge(marker: IMarker): SnBadgeConfig {
    return this._badgeService.getBadge(marker);
  }

  public createBadge(marker: IMarker, badgeClass?: string): void {
    const esriMarker = this._esriMarkers.get(marker.id);
    if (esriMarker) {
      this._badgeService.createBadge(esriMarker, marker, badgeClass);
    }
  }

  public createSelectedBadge(marker: IMarker): void {
    const esriMarker = this._esriMarkers.get(marker.id);
    if (esriMarker) {
      this._badgeService.createSelectedBadge(esriMarker, marker);
    }
  }

  public removeSelectedBadge(marker: IMarker): void {
    const esriMarker = this._esriMarkers.get(marker.id);
    if (esriMarker) {
      this._badgeService.removeSelectedBadge(esriMarker, marker);
    }
  }

  public setBadgeStatus(
    marker: IMarker,
    status: string,
    badgePosition: Record<string, unknown>,
  ): void {
    const esriMarker = this._esriMarkers.get(marker.id);
    if (esriMarker) {
      this._badgeService.setBadgeStatus(esriMarker, marker, status, badgePosition);
    }
  }

  public setPopup(marker: IMarker, popup: FsMapPopup<EsriMap>): void {
    const esriMarker = this._esriMarkers.get(marker.id);
    if (esriMarker) {
      marker.popup = popup;
      this._setupMarkerPopupClickHandler(esriMarker, marker, popup);
    }
  }

  // Event handling methods - these would need proper ESRI event implementation
  public onMarkerClick(marker: IMarker): Observable<IMarkerClickEvent<EsriMap>> {
    return new Observable();
  }

  public onMarkerDoubleClick(marker: IMarker): Observable<IMarkerDoubleClickEvent<EsriMap>> {
    return new Observable();
  }

  public onMarkerRightClick(marker: IMarker): Observable<IMarkerRightClickEvent<EsriMap>> {
    return new Observable();
  }

  public onMarkerMouseOver(marker: IMarker): Observable<IMarkerMouseOverEvent<EsriMap>> {
    return new Observable();
  }

  public onMarkerMouseOut(marker: IMarker): Observable<IMarkerMouseOutEvent<EsriMap>> {
    return new Observable();
  }

  public onMarkerMouseEnter(marker: IMarker): Observable<IMarkerMouseEnterEvent<EsriMap>> {
    return new Observable();
  }

  public onMarkerMouseLeave(marker: IMarker): Observable<IMarkerMouseLeaveEvent<EsriMap>> {
    return new Observable();
  }

  public onMarkerDragStart(marker: IMarker): Observable<IMarkerDragStartEvent<EsriMap>> {
    return new Observable();
  }

  public onMarkerDrag(marker: IMarker): Observable<IMarkerDragMoveEvent<EsriMap>> {
    return new Observable();
  }

  public onMarkerDragEnd(marker: IMarker): Observable<IMarkerDragEndEvent<EsriMap>> {
    return new Observable();
  }

  public onMarkerPositionChange(marker: IMarker): Observable<IMarkerPositionChangeEvent<EsriMap>> {
    return new Observable();
  }

  public onMarkerTitleChange(marker: IMarker): Observable<IMarkerTitleChangeEvent<EsriMap>> {
    return new Observable();
  }

  public onMarkerVisibilityChange(
    marker: IMarker,
  ): Observable<IMarkerVisibilityChangeEvent<EsriMap>> {
    return new Observable();
  }

  public onMarkerSelectionChange(
    marker: IMarker,
  ): Observable<IMarkerSelectionChangeEvent<EsriMap>> {
    return new Observable();
  }

  public onMarkerIconChange(marker: IMarker): Observable<IMarkerIconChangeEvent<EsriMap>> {
    return new Observable();
  }

  public onMarkerStyleChange(marker: IMarker): Observable<IMarkerStyleChangeEvent<EsriMap>> {
    return new Observable();
  }

  public onMarkerAdd(marker: IMarker): Observable<IMarkerAddEvent<EsriMap>> {
    return new Observable();
  }

  public onMarkerRemove(marker: IMarker): Observable<IMarkerRemoveEvent<EsriMap>> {
    return new Observable();
  }

  public onMarkerDestroy(marker: IMarker): Observable<IMarkerDestroyEvent<EsriMap>> {
    return new Observable();
  }

  public onMarkerEvents(marker: IMarker): Observable<IMarkerEventUnion<EsriMap>> {
    return new Observable();
  }

  public findMarkerById(id: string): IMarker | null {
    return this._queryService.findMarkerById(id);
  }

  public getAllMarkers(): IMarker[] {
    return this._queryService.getAllMarkers();
  }

  public getMarkersByAttribute(attribute: string, value: unknown): IMarker[] {
    return this._queryService.getMarkersByAttribute(attribute, value);
  }

  public getMarkersByEntityType(entityType: string): IMarker[] {
    return this._queryService.getMarkersByEntityType(entityType);
  }

  public getVisibleMarkers(): IMarker[] {
    return this._queryService.getVisibleMarkers();
  }

  public getMarkersInBounds(bounds: {
    north: number;
    south: number;
    east: number;
    west: number;
  }): IMarker[] {
    return this._queryService.getMarkersInBounds(bounds);
  }

  public getMarkersWithinDistance(
    center: { lat: number; lon: number },
    distanceKm: number,
  ): IMarker[] {
    return this._queryService.getMarkersWithinDistance(center, distanceKm);
  }

  public searchMarkers(query: string): IMarker[] {
    return this._queryService.searchMarkers(query);
  }

  public calculateDistance(
    marker1: IMarker,
    marker2: IMarker,
    unit: DistanceUnit = DistanceUnit.MILES,
  ): number {
    // Delegate to the geometry service for proper distance calculation
    return this._geometryService.calculateDistance(marker1, marker2, unit);
  }

  public setFollowMode(marker: IMarker, followMode: boolean): void {
    const esriMarker = this._esriMarkers.get(marker.id);
    if (esriMarker && esriMarker.setFollowMode) {
      esriMarker.setFollowMode(followMode);
    }
  }

  public getFollowMode(marker: IMarker): boolean {
    const esriMarker = this._esriMarkers.get(marker.id);
    if (esriMarker && esriMarker.getFollowMode) {
      return esriMarker.getFollowMode();
    }
    return false;
  }

  public setDestination(marker: IMarker, dest: SnLonLatLike): void {
    const esriMarker = this._esriMarkers.get(marker.id);
    if (esriMarker && esriMarker.setDestination) {
      esriMarker.setDestination(dest);
    }
  }

  public setDeltas(marker: IMarker, seconds: number): void {
    const esriMarker = this._esriMarkers.get(marker.id);
    if (esriMarker && esriMarker.setDeltas) {
      esriMarker.setDeltas(seconds);
    }
  }

  public move(marker: IMarker): void {
    const esriMarker = this._esriMarkers.get(marker.id);
    if (esriMarker && esriMarker.move) {
      esriMarker.move();
    }
  }

  public animateSetLonLat(marker: IMarker, seconds: number, moveto: SnLonLatLike): void {
    const esriMarker = this._esriMarkers.get(marker.id);
    if (esriMarker && esriMarker.animateSetLonLat) {
      esriMarker.animateSetLonLat(seconds, moveto);
    }
  }

  public isWithinMapBounds(marker: IMarker): boolean {
    const esriMarker = this._esriMarkers.get(marker.id);
    if (esriMarker && esriMarker.isWithinMapBounds) {
      return esriMarker.isWithinMapBounds();
    }
    return true;
  }

  public getAnimationState(marker: IMarker): {
    isAnimating: boolean;
    destination?: SnLonLat;
    seconds?: number;
  } {
    const esriMarker = this._esriMarkers.get(marker.id);
    if (esriMarker && esriMarker.getAnimationState) {
      return esriMarker.getAnimationState();
    }
    return { isAnimating: false };
  }

  public destroy(): void {
    this._destroy$.next();
    this._destroy$.complete();

    this.getAllMarkers().forEach((marker) => this.removeMarker(marker));

    this._esriMarkers.clear();
    this._eventService.destroy();
  }

  private _generateMarkerId(): string {
    return `esri_marker_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private _createMarkerData(id: string, options: IMarkerOptions, esriMarker: IEsriMarker): IMarker {
    return {
      id,
      position: options.lonLat ? SnLonLat.convert(options.lonLat) : new SnLonLat(0, 0),
      visible: true,
      icon: options.icon,
      color: options.color,
      size: options.size,
      rotation: options.rotation,
      draggable: options.draggable ?? false,
      clickable: options.clickable ?? true,
      title: options.title,
      content: options.content,
      zIndex: options.zIndex,
      styles: options.style,
      customClass: options.animationClass,
      customStyle: {},
      iconLabel: options.iconLabel,
      tooltip: options.showTooltipOnHover ? 'hover' : undefined,
      attributes: options.attributes,
      metadata: {
        entityType: options.cluster?.properties?.objects ? 'cluster' : 'marker',
        entityId: options.cluster?.id?.toString() || id,
        source: 'esri-marker-adapter',
        tags: [],
        category: 'marker',
        priority: options.priority === 'high' ? 1 : options.priority === 'low' ? 3 : 2,
        customData: {
          clusterObjects: options.cluster?.properties?.objects || 0,
        },
      },
      options,
      badge: undefined,
      popup: undefined,
      selected: options.initialSelected ?? false,
      highlighted: options.initialHighlighted ?? false,
      focused: options.initialFocused ?? false,
      priority: options.priority ?? 'normal',
      lazyLoaded: options.lazyLoad ?? false,
      eventHandlers: {
        click: false,
        doubleClick: false,
        rightClick: false,
        mouseOver: false,
        mouseOut: false,
        mouseEnter: false,
        mouseLeave: false,
        dragStart: false,
        drag: false,
        dragEnd: false,
        focus: false,
        blur: false,
        positionChange: false,
        titleChange: false,
        visibilityChange: false,
        selectionChange: false,
        iconChange: false,
        styleChange: false,
        ...options.eventHandlers,
      },
      ariaLabel: options.title,
      role: options.clickable ? 'button' : 'img',
      tabIndex: options.clickable ? 0 : -1,
      created: new Date(),
      lastModified: new Date(),
      version: 1,
    };
  }

  private _createEsriMarker(options: IAdvancedEsriFeatureOptions): IEsriMarker {
    return this._markerFactory.createFeatureMarker({
      ...options,
      map: this._map,
    });
  }

  private _convertToEsriMarkerOptions(options: IMarkerOptions): IAdvancedEsriFeatureOptions {
    return {
      id: options.id,
      position: options.lonLat,
      title: options.title,
      draggable: options.draggable,
      zIndex: options.zIndex,
      icon: options.icon
        ? {
            fontSet: (options.icon as any).fontSet,
            fontIcon: (options.icon as any).fontIcon,
            color: options.color,
            url: (options.icon as any).url,
            size: options.size,
          }
        : undefined,
      size: options.size,
      color: options.color,
      rotation: options.rotation,
      styles: options.style,
      cluster: options.cluster,
      content: options.content,
      layer: (options as any).layer,
      organization: (options as any).organization,
      entityType: options.cluster?.properties?.objects ? 'cluster' : 'marker',
      entityId: options.cluster?.id?.toString() || options.id,
      priority: options.priority,
      category: 'marker',
      tags: [],
      customData: {
        clusterObjects: options.cluster?.properties?.objects || 0,
        attributes: options.attributes,
      },
    };
  }

  private _setupMarkerPopupClickHandler(
    esriMarker: IEsriMarker,
    marker: IMarker,
    popup: FsMapPopup<EsriMap>,
  ): void {
    // ESRI popup handling would be implemented here
    // This is a placeholder for now
  }
}
